// All the game's sound, synthesised. The sprite pack draws itself rather than
// shipping pixels; the audio does the same, so there is still not one asset
// request in the whole game.
//
// Everything is built from two primitives — a shaped oscillator and a filtered
// noise burst — plus a pair of always-on ambience beds that cross-fade between
// day, night and rain. A browser will not start an AudioContext before the
// player interacts, so `unlock()` is wired to the first key or click.

type Waveform = OscillatorType;

export type SoundId =
  | "step_grass"
  | "step_wood"
  | "step_stone"
  | "step_water"
  | "hoe"
  | "chop"
  | "pickaxe"
  | "water"
  | "plant"
  | "harvest"
  | "coin"
  | "buy"
  | "error"
  | "ui"
  | "cast"
  | "bite"
  | "reel"
  | "splash"
  | "eat"
  | "faint"
  | "build"
  | "sleep"
  | "dawn"
  | "thunder"
  | "cluck"
  | "moo"
  | "levelup";

export type Ambience = "day" | "night" | "rain" | "cave" | "none";

const VOLUME_KEY = "farmstead.volume.v1";

export class Audio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private ambBus: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private ambience: Ambience = "none";
  private ambNodes: { stop: () => void } | null = null;
  private lastStep = 0;

  masterVolume = 0.7;
  sfxVolume = 0.9;
  musicVolume = 0.45;
  muted = false;

  constructor() {
    this.loadVolumes();
    const unlock = () => this.unlock();
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
  }

  /** Safe to call at any time; the first successful call builds the graph. */
  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === "suspended") void this.ctx.resume();
      return;
    }
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    try {
      this.ctx = new Ctor();
    } catch {
      return;
    }
    this.master = this.ctx.createGain();
    this.sfxBus = this.ctx.createGain();
    this.ambBus = this.ctx.createGain();
    this.sfxBus.connect(this.master);
    this.ambBus.connect(this.master);
    this.master.connect(this.ctx.destination);
    this.applyVolumes();

    // one second of white noise, reused by every percussive sound
    const rate = this.ctx.sampleRate;
    const buf = this.ctx.createBuffer(1, rate, rate);
    const data = buf.getChannelData(0);
    let seed = 12345;
    for (let i = 0; i < data.length; i++) {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      data[i] = (seed / 0x3fffffff - 1) * 0.6;
    }
    this.noiseBuffer = buf;

    const pending = this.ambience;
    this.ambience = "none";
    this.setAmbience(pending);
  }

  get enabled(): boolean {
    return this.ctx !== null && !this.muted;
  }

  // ------------------------------------------------------------- volumes

  setVolumes(o: { master?: number; sfx?: number; music?: number; muted?: boolean }): void {
    if (o.master !== undefined) this.masterVolume = clamp01(o.master);
    if (o.sfx !== undefined) this.sfxVolume = clamp01(o.sfx);
    if (o.music !== undefined) this.musicVolume = clamp01(o.music);
    if (o.muted !== undefined) this.muted = o.muted;
    this.applyVolumes();
    this.saveVolumes();
  }

  private applyVolumes(): void {
    if (!this.ctx || !this.master || !this.sfxBus || !this.ambBus) return;
    const t = this.ctx.currentTime;
    this.master.gain.setTargetAtTime(this.muted ? 0 : this.masterVolume, t, 0.02);
    this.sfxBus.gain.setTargetAtTime(this.sfxVolume, t, 0.02);
    this.ambBus.gain.setTargetAtTime(this.musicVolume, t, 0.05);
  }

  private loadVolumes(): void {
    try {
      const raw = localStorage.getItem(VOLUME_KEY);
      if (!raw) return;
      const v = JSON.parse(raw);
      this.masterVolume = clamp01(v.master ?? this.masterVolume);
      this.sfxVolume = clamp01(v.sfx ?? this.sfxVolume);
      this.musicVolume = clamp01(v.music ?? this.musicVolume);
      this.muted = !!v.muted;
    } catch {
      /* storage disabled */
    }
  }

  private saveVolumes(): void {
    try {
      localStorage.setItem(
        VOLUME_KEY,
        JSON.stringify({ master: this.masterVolume, sfx: this.sfxVolume, music: this.musicVolume, muted: this.muted }),
      );
    } catch {
      /* storage disabled */
    }
  }

  // -------------------------------------------------------------- effects

  play(id: SoundId, detune = 0): void {
    if (!this.ctx || !this.sfxBus || this.muted) return;
    const t = this.ctx.currentTime;
    const d = 2 ** (detune / 12);
    switch (id) {
      case "step_grass":
        this.noise(t, 0.07, "lowpass", 1100 * d, 0.16, 500);
        break;
      case "step_wood":
        this.noise(t, 0.06, "bandpass", 1700 * d, 0.2, 900);
        this.tone(t, 0.05, "sine", 180 * d, 0.1, 120);
        break;
      case "step_stone":
        this.noise(t, 0.05, "highpass", 2400 * d, 0.14);
        break;
      case "step_water":
        this.noise(t, 0.14, "lowpass", 800 * d, 0.18, 260);
        break;
      case "hoe":
        this.noise(t, 0.16, "lowpass", 900, 0.34, 220);
        this.tone(t, 0.12, "triangle", 150, 0.18, 70);
        break;
      case "chop":
        this.noise(t, 0.09, "bandpass", 1300, 0.34, 700);
        this.noise(t + 0.05, 0.2, "lowpass", 600, 0.24, 160);
        this.tone(t, 0.14, "square", 120, 0.1, 60);
        break;
      case "pickaxe":
        this.noise(t, 0.07, "highpass", 3000, 0.3, 1600);
        this.tone(t, 0.1, "triangle", 320, 0.14, 140);
        break;
      case "water":
        this.noise(t, 0.55, "bandpass", 1800, 0.26, 420);
        break;
      case "plant":
        this.noise(t, 0.08, "lowpass", 1400, 0.18, 600);
        this.tone(t, 0.09, "sine", 420, 0.12, 620);
        break;
      case "harvest":
        this.tone(t, 0.1, "sine", 660, 0.2, 990);
        this.tone(t + 0.06, 0.12, "sine", 990, 0.14, 1320);
        break;
      case "coin":
        this.tone(t, 0.07, "square", 880, 0.12);
        this.tone(t + 0.06, 0.12, "square", 1320, 0.1);
        break;
      case "buy":
        this.tone(t, 0.08, "triangle", 520, 0.14);
        this.tone(t + 0.07, 0.14, "triangle", 780, 0.12);
        break;
      case "error":
        this.tone(t, 0.16, "square", 190, 0.14, 120);
        break;
      case "ui":
        this.tone(t, 0.04, "square", 720, 0.07);
        break;
      case "cast":
        this.noise(t, 0.22, "bandpass", 2600, 0.18, 900);
        break;
      case "bite":
        this.tone(t, 0.09, "triangle", 500, 0.18, 900);
        this.tone(t + 0.08, 0.09, "triangle", 900, 0.14, 620);
        break;
      case "reel":
        this.noise(t, 0.05, "bandpass", 3200, 0.12);
        break;
      case "splash":
        this.noise(t, 0.32, "lowpass", 1600, 0.3, 300);
        this.tone(t, 0.16, "sine", 300, 0.1, 130);
        break;
      case "eat":
        this.noise(t, 0.1, "lowpass", 700, 0.2, 300);
        this.noise(t + 0.12, 0.1, "lowpass", 600, 0.16, 260);
        break;
      case "faint":
        this.tone(t, 0.7, "sawtooth", 320, 0.2, 70);
        break;
      case "build":
        this.noise(t, 0.08, "bandpass", 1500, 0.26, 800);
        this.tone(t + 0.05, 0.12, "square", 260, 0.12, 200);
        break;
      case "sleep":
        this.tone(t, 0.5, "sine", 300, 0.14, 150);
        break;
      case "dawn":
        [523, 659, 784, 1047].forEach((f, i) => this.tone(t + i * 0.11, 0.28, "triangle", f, 0.13));
        break;
      case "levelup":
        [659, 880, 1175].forEach((f, i) => this.tone(t + i * 0.08, 0.24, "square", f, 0.09));
        break;
      case "thunder":
        this.noise(t, 1.4, "lowpass", 420, 0.4, 90);
        break;
      case "cluck":
        this.tone(t, 0.06, "square", 900, 0.08, 1200);
        this.tone(t + 0.08, 0.05, "square", 1100, 0.06, 800);
        break;
      case "moo":
        this.tone(t, 0.55, "sawtooth", 170, 0.12, 130);
        break;
    }
  }

  /** Footsteps rate-limit themselves so a held key does not machine-gun. */
  step(surface: "grass" | "wood" | "stone" | "water", now: number): void {
    if (now - this.lastStep < 0.29) return;
    this.lastStep = now;
    this.play(`step_${surface}` as SoundId, (now * 13) % 3);
  }

  // ------------------------------------------------------------ ambience

  setAmbience(kind: Ambience): void {
    if (kind === this.ambience) return;
    this.ambience = kind;
    if (!this.ctx || !this.ambBus) return;
    this.ambNodes?.stop();
    this.ambNodes = null;
    if (kind === "none") return;

    const ctx = this.ctx;
    const out = ctx.createGain();
    out.gain.value = 0;
    out.connect(this.ambBus);
    out.gain.setTargetAtTime(kind === "rain" ? 0.5 : 0.28, ctx.currentTime, 1.2);

    const stops: (() => void)[] = [];
    if (kind === "rain" && this.noiseBuffer) {
      const src = ctx.createBufferSource();
      src.buffer = this.noiseBuffer;
      src.loop = true;
      const f = ctx.createBiquadFilter();
      f.type = "bandpass";
      f.frequency.value = 1400;
      f.Q.value = 0.6;
      src.connect(f).connect(out);
      src.start();
      stops.push(() => src.stop());
    } else {
      // a slow two-note drone, a fifth apart, detuned so it breathes
      const roots = kind === "night" ? [110, 164.8] : kind === "cave" ? [82, 98] : [146.8, 220];
      for (const [i, f] of roots.entries()) {
        const osc = ctx.createOscillator();
        osc.type = kind === "cave" ? "sine" : "triangle";
        osc.frequency.value = f;
        const g = ctx.createGain();
        g.gain.value = i === 0 ? 0.5 : 0.3;
        const lfo = ctx.createOscillator();
        lfo.frequency.value = 0.07 + i * 0.03;
        const lfoGain = ctx.createGain();
        lfoGain.gain.value = f * 0.008;
        lfo.connect(lfoGain).connect(osc.frequency);
        const lp = ctx.createBiquadFilter();
        lp.type = "lowpass";
        lp.frequency.value = kind === "night" ? 700 : 1400;
        osc.connect(g).connect(lp).connect(out);
        osc.start();
        lfo.start();
        stops.push(() => {
          osc.stop();
          lfo.stop();
        });
      }
    }

    this.ambNodes = {
      stop: () => {
        out.gain.setTargetAtTime(0, ctx.currentTime, 0.4);
        setTimeout(() => {
          for (const s of stops) {
            try {
              s();
            } catch {
              /* already stopped */
            }
          }
          out.disconnect();
        }, 1200);
      },
    };
  }

  // ------------------------------------------------------------ primitives

  private tone(at: number, dur: number, type: Waveform, freq: number, gain: number, slideTo?: number): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, at);
    if (slideTo !== undefined) osc.frequency.exponentialRampToValueAtTime(Math.max(20, slideTo), at + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(gain, at + Math.min(0.012, dur * 0.3));
    g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    osc.connect(g).connect(this.sfxBus!);
    osc.start(at);
    osc.stop(at + dur + 0.02);
  }

  private noise(
    at: number,
    dur: number,
    filter: BiquadFilterType,
    freq: number,
    gain: number,
    sweepTo?: number,
  ): void {
    const ctx = this.ctx!;
    if (!this.noiseBuffer) return;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = filter;
    f.frequency.setValueAtTime(freq, at);
    if (sweepTo !== undefined) f.frequency.exponentialRampToValueAtTime(Math.max(40, sweepTo), at + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, at);
    g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    src.connect(f).connect(g).connect(this.sfxBus!);
    src.start(at);
    src.stop(at + dur + 0.02);
  }
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}
